import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Icon } from "../Icon";
import type { IconName } from "@/types/icons";
import { ImageProps } from "@/types/image";
import { cva, type VariantProps } from "class-variance-authority";
import { LinkData } from "@/types";
import { Heading } from "../Heading";

const newsCardVariants = cva(
  "relative flex flex-col gap-xs rounded-lg overflow-hidden text-white bg-black",
  {
    variants: {
      align: {
        top: "justify-start",
        bottom: "justify-end",
      },
      aspect: {
        "2/3": "aspect-4/5 md:aspect-2/3",
        "2/1": "aspect-4/5 md:aspect-2/1",
      },
    },
    defaultVariants: {
      align: "bottom",
      aspect: "2/3",
    },
  }
);

const linkVariants = cva(
  "group block no-underline rounded-lg outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2",
  {
    variants: {
      variant: {
        dark: "outline-purple",
        light: "outline-yellow",
      },
    },
  }
);

const contentVariants = cva(
  "bg-black/50 backdrop-blur-sm flex flex-col gap-xs",
  {
    variants: {
      padding: {
        md: "p-md",
        lg: "p-lg",
      },
    },
    defaultVariants: {
      padding: "md",
    },
  }
);

interface NewsCardProps
  extends
    VariantProps<typeof newsCardVariants>,
    VariantProps<typeof contentVariants> {
  variant?: "light" | "dark";
  align: "top" | "bottom";
  image?: ImageProps;
  icon?: IconName;
  headline: string | React.ReactNode;
  label: string | React.ReactNode;
  href?: LinkData["href"];
  target?: LinkData["target"];
  className?: string;
  buttonText?: string;
}

export const NewsCard: React.FC<NewsCardProps> = ({
  variant = "light",
  align = "top",
  aspect = "2/3",
  padding = "lg",
  image,
  icon,
  headline,
  label,
  href,
  target,
  className = "",
  buttonText = "",
}) => {
  const content = (
    <article
      className={newsCardVariants({
        align,
        aspect,
        className: `group cursor-pointer ${className}`,
      })}
    >
      {image && (
        <Image
          src={image.src}
          alt={image.alt}
          fill
          sizes={
            aspect === "2/1"
              ? "(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 60vw"
              : "(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 50vw"
          }
          className="absolute object-cover transition-transform duration-500 group-hover:scale-110 group-focus-visible:scale-110"
          priority={false}
        />
      )}
      <div
        className={`flex flex-col transform transition-transform duration-300 absolute -top-px -bottom-px -right-px -left-px ${
          align === "top" ? "justify-start" : "justify-end"
        }`}
      >
        <div className={contentVariants({ padding })}>
          {/* this element fixes a sub-pixel issue that would cause the border of the image to be unblurred*/}
          <div className="absolute -inset-0.5 rounded-none -z-10" />

          <p className="text-sm flex content-center text-white/80">
            {icon && (
              <Icon
                name={icon}
                size={18}
                decorative
                aria-hidden="true"
                className="inline mr-1"
              />
            )}
            {label}
          </p>
          <Heading
            style="h5"
            tag="h3"
            className="wrap-break-word transition-colors"
          >
            {headline}
          </Heading>
          {buttonText && (
            <div className="mt-4">
              <div className="px-md py-sm rounded-4xl transition-all duration-200 inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 font-weight-medium outline-yellow bg-yellow text-black hover:bg-white hover:text-black">
                <Icon
                  name="arrow-right"
                  size={18}
                  decorative
                  aria-hidden="true"
                  className="inline mr-2"
                />
                {buttonText}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={linkVariants({ variant, className })}
      >
        {content}
      </Link>
    );
  }

  return content;
};
